import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

// =============================================================================
// TYPES
// =============================================================================

export type FeatureCategory = 'product' | 'portal' | 'admin' | 'distillation' | 'limit'
export type AttestationFramework = 'none' | 'HIPAA' | 'EEOC' | 'clinical' | 'regulatory'

export interface FeatureDefinition {
  key: string
  name: string
  description: string
  category: FeatureCategory
  requires_trial: boolean
  requires_pro: boolean
  requires_enterprise: boolean
  attestation_framework: AttestationFramework | null
  is_premium: boolean
  display_order: number
  created_at?: string
  updated_at?: string
}

export interface CreateFeatureRequest {
  key: string
  name: string
  description: string
  category: FeatureCategory
  requires_trial?: boolean
  requires_pro?: boolean
  requires_enterprise?: boolean
  attestation_framework?: AttestationFramework | null
  is_premium?: boolean
  display_order?: number
}

export interface UpdateFeatureRequest {
  name?: string
  description?: string
  category?: FeatureCategory
  requires_trial?: boolean
  requires_pro?: boolean
  requires_enterprise?: boolean
  attestation_framework?: AttestationFramework | null
  is_premium?: boolean
  display_order?: number
}

interface UseFeaturesOptions {
  category?: FeatureCategory
}

// =============================================================================
// QUERY KEYS
// =============================================================================

const QUERY_KEYS = {
  features: (options: UseFeaturesOptions) => ['admin-features', options],
  feature: (key: string) => ['admin-feature', key],
}

// =============================================================================
// FEATURE QUERIES
// =============================================================================

/**
 * Fetch list of feature definitions
 */
export function useFeatures(options: UseFeaturesOptions = {}) {
  const { category } = options

  return useQuery<FeatureDefinition[]>({
    queryKey: QUERY_KEYS.features({ category }),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (category) params.append('category', category)

      const res = await api.get(`/admin/features?${params.toString()}`)
      return res.data
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - features don't change very often
  })
}

/**
 * Fetch single feature definition
 */
export function useFeature(key: string | null) {
  return useQuery<FeatureDefinition>({
    queryKey: QUERY_KEYS.feature(key || ''),
    queryFn: async () => {
      const res = await api.get(`/admin/features/${key}`)
      return res.data
    },
    enabled: !!key,
  })
}

// =============================================================================
// FEATURE MUTATIONS
// =============================================================================

/**
 * Create a new feature definition
 */
export function useCreateFeature() {
  const queryClient = useQueryClient()

  return useMutation<FeatureDefinition, Error, CreateFeatureRequest>({
    mutationFn: async (data) => {
      const res = await api.post('/admin/features', data)
      return res.data
    },
    onSuccess: () => {
      // Invalidate all feature queries
      queryClient.invalidateQueries({ queryKey: ['admin-features'] })
    },
  })
}

/**
 * Update an existing feature definition
 */
export function useUpdateFeature() {
  const queryClient = useQueryClient()

  return useMutation<FeatureDefinition, Error, { key: string; data: UpdateFeatureRequest }>({
    mutationFn: async ({ key, data }) => {
      const res = await api.put(`/admin/features/${key}`, data)
      return res.data
    },
    onSuccess: (_, variables) => {
      // Invalidate feature queries
      queryClient.invalidateQueries({ queryKey: ['admin-features'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.feature(variables.key) })
    },
  })
}

/**
 * Delete a feature definition
 */
export function useDeleteFeature() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (key) => {
      await api.delete(`/admin/features/${key}`)
    },
    onSuccess: () => {
      // Invalidate all feature queries
      queryClient.invalidateQueries({ queryKey: ['admin-features'] })
    },
  })
}
